<div class="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4">
  <div class="max-w-7xl mx-auto">
    <!-- Header -->
    <div
      class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8"
    >
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white">Mes événements</h1>
        <p class="text-gray-600 dark:text-gray-400 mt-1">
          {{ filteredEvents.length }} événement{{
            filteredEvents.length !== 1 ? "s" : ""
          }}
        </p>
      </div>
      <button
        (click)="createEvent()"
        class="px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg font-bold hover:from-purple-700 hover:to-purple-800 transition flex items-center gap-2"
      >
        <svg
          class="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 4v16m8-8H4"
          />
        </svg>
        Nouvel événement
      </button>
    </div>

    <!-- Confirmation modal -->
    <app-confirm-action-modal
      [show]="showConfirm"
      [title]="'Supprimer l\'événement'"
      [message]="
        selectedEventForDelete
          ? 'Voulez-vous vraiment supprimer ' +
            selectedEventForDelete.name +
            ' ? Cette action est irréversible.'
          : ''
      "
      [confirmLabel]="'Supprimer'"
      [cancelLabel]="'Annuler'"
      (confirm)="onConfirmDelete()"
      (cancel)="onCancelDelete()"
    ></app-confirm-action-modal>

    <!-- Filtres -->
    <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 p-4 mb-6">
      <div class="grid md:grid-cols-4 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >Type</label
          >
          <select
            [(ngModel)]="filterType"
            (ngModelChange)="applyFilters()"
            class="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-600"
          >
            <option value="">Tous les types</option>
            @for (t of eventTypes; track t) {
              <option [value]="t.value">{{ t.icon }} {{ t.label }}</option>
            }
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >Statut</label
          >
          <select
            [(ngModel)]="filterStatus"
            (ngModelChange)="applyFilters()"
            class="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-600"
          >
            <option value="all">Tous</option>
            <option value="upcoming">⏳ À venir</option>
            <option value="completed">✓ Terminés</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >Période</label
          >
          <select
            [(ngModel)]="filterPeriod"
            (ngModelChange)="applyFilters()"
            class="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-600"
          >
            <option value="all">Toutes</option>
            <option value="7days">7 prochains jours</option>
            <option value="30days">30 prochains jours</option>
            <option value="3months">3 prochains mois</option>
            <option value="past30">30 derniers jours</option>
            <option value="past3months">3 derniers mois</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >Recherche</label
          >
          <input
            type="text"
            [(ngModel)]="searchQuery"
            (ngModelChange)="applyFilters()"
            placeholder="Nom de l'événement..."
            class="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-600"
          />
        </div>
      </div>
    </div>

    <!-- Loading -->
    @if (isLoading) {
      <div class="flex justify-center py-20">
        <svg
          class="animate-spin h-10 w-10 text-purple-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          ></path>
        </svg>
      </div>
    }

    <!-- Erreur -->
    @if (errorMessage && !isLoading) {
      <div
        class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center text-red-700 dark:text-red-300"
      >
        {{ errorMessage }}
      </div>
    }

    <!-- Liste -->
    @if (!isLoading && !errorMessage) {
      <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        @for (event of filteredEvents; track event) {
          <div
            class="bg-white dark:bg-slate-800 rounded-xl shadow-md dark:shadow-slate-900/50 hover:shadow-xl dark:shadow-slate-900/60 transition-all duration-300 overflow-hidden group"
          >
            <div class="p-6">
              <!-- Header -->
              <div class="flex justify-between items-start mb-4">
                <div class="flex-1 min-w-0 pr-2">
                  <h3
                    class="text-lg font-bold text-gray-900 dark:text-white mb-1 group-hover:text-purple-600 transition truncate"
                  >
                    {{ event.name }}
                  </h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    📅 {{ formatDate(event.eventDate) }}
                  </p>
                </div>
                <span
                  class="shrink-0 px-2 py-1 text-xs font-medium rounded-full"
                  [ngClass]="getStatusClasses(resolveDisplayStatus(event))"
                >
                  {{ getStatusLabel(resolveDisplayStatus(event)) }}
                </span>
              </div>
              <!-- Infos -->
              <div class="space-y-1 mb-4 text-sm text-gray-600 dark:text-gray-400">
                <div class="flex items-center gap-2">
                  <svg
                    class="w-4 h-4 shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>{{ event.startTime }} – {{ event.endTime }}</span>
                </div>
                @if (event.location) {
                  <div class="flex items-center gap-2">
                    <svg
                      class="w-4 h-4 shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <span class="truncate">{{
                      event.location && $any(event.location).city
                        ? $any(event.location).city
                        : event.location && $any(event.location).address
                          ? $any(event.location).address
                          : event.location
                    }}</span>
                  </div>
                }
                <div class="flex items-center gap-2">
                  <span>{{ getEventTypeDetails(event.eventType)?.icon }}</span>
                  <span>{{ getEventTypeDetails(event.eventType)?.label }}</span>
                </div>
              </div>
              <!-- Revenue si analysé -->
              @if (event.actualRevenue) {
                <div
                  class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-2 mb-4 text-sm"
                >
                  <span class="text-green-800 dark:text-green-300 font-medium"
                    >CA réel : {{ event.actualRevenue }}€</span
                  >
                </div>
              }
              <!-- Badge source opportunité -->
              @if (event.source === "opportunity") {
                <div class="mb-3">
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs rounded-full"
                  >
                    📍 Via opportunité
                  </span>
                </div>
              }
              <!-- CTA -->
              <div class="pt-3 border-t">
                <div class="flex items-center gap-3">
                  @if (isCompleted(event)) {
                    <button
                      type="button"
                      (click)="viewRapport(event.id); $event.stopPropagation()"
                      class="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline cursor-pointer bg-transparent border-0 p-0"
                    >
                      Voir rapport →
                    </button>
                  } @else {
                    <button
                      type="button"
                      (click)="viewEvent(event.id); $event.stopPropagation()"
                      class="text-sm font-medium text-purple-600 hover:underline cursor-pointer bg-transparent border-0 p-0"
                    >
                      Voir les détails →
                    </button>
                  }
                  <button
                    type="button"
                    (click)="$event.stopPropagation(); openConfirm(event)"
                    class="ml-auto text-sm font-medium text-red-600 dark:text-red-400 hover:underline bg-transparent border-0 p-0"
                    title="Supprimer l'événement"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          </div>
        }
        <!-- Empty state -->
        @if (filteredEvents.length === 0) {
          <div class="col-span-full">
            <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm dark:shadow-slate-900/30 p-12 text-center">
              <div class="text-6xl mb-4">📅</div>
              <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">
                Aucun événement trouvé
              </h3>
              <p class="text-gray-600 dark:text-gray-400 mb-6">
                @if (searchQuery || filterType || filterStatus !== "all") {
                  Essaie d'autres filtres.
                } @else {
                  Crée ton premier événement.
                }
              </p>
              @if (!searchQuery && !filterType && filterStatus === "all") {
                <button
                  (click)="createEvent()"
                  class="px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg font-bold hover:from-purple-700 hover:to-purple-800 transition"
                >
                  ✨ Créer mon 1er événement
                </button>
              }
            </div>
          </div>
        }
      </div>
    }
  </div>
</div>
